import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { toast } from 'sonner';
import { clearReferral, getReferral } from '../../utils/referral';

const BASE_URL = import.meta.env.VITE_APP_API_URL;
const SCANNED_QRS_KEY = 'authx_scanned_qrs';

const getCustomerToken = () => localStorage.getItem('authx_customer_token');

const getScannedQRs = () => {
  try {
    return JSON.parse(sessionStorage.getItem(SCANNED_QRS_KEY) || '[]');
  } catch {
    return [];
  }
};

const addScannedQR = (qrCode) => {
  try {
    const existing = getScannedQRs();
    if (!existing.includes(qrCode)) {
      sessionStorage.setItem(SCANNED_QRS_KEY, JSON.stringify([...existing, qrCode]));
    }
  } catch {}
};

export const isQRAlreadyScannedThisSession = (qrCode) => {
  return getScannedQRs().includes(qrCode);
};

const customerApi = axios.create({ baseURL: BASE_URL });
customerApi.interceptors.request.use((cfg) => {
  const t = getCustomerToken();
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

const t = (name, fn) =>
  createAsyncThunk(name, async (arg, { rejectWithValue }) => {
    try {
      return await fn(arg);
    } catch (err) {
      toast.error(err.response?.data?.message || err.message || 'Request failed');
      return rejectWithValue(err.response?.data?.message || err.message);
    }
  });

export const sendOtp = t('loyalty/sendOtp', async (dto) => {
  const { data } = await customerApi.post('Loyalty/send-otp', dto);
  return data;
});

export const verifyOtp = t('loyalty/verifyOtp', async (dto) => {
  const { data } = await customerApi.post('Loyalty/verify-otp', { referralCode: getReferral() || undefined, ...dto });
  if (data.data?.accessToken) clearReferral(); // an existing customer cannot be referred any more, a new one just was
  if (data.data?.accessToken) {
    localStorage.setItem('authx_customer_token', data.data.accessToken);
    localStorage.setItem('authx_customer_refresh', data.data.refreshToken);
    localStorage.setItem('authx_customer', JSON.stringify(data.data.customer));
  }
  return data.data;
});

export const registerCustomer = t('loyalty/register', async (dto) => {
  const { data } = await customerApi.post('Loyalty/register', dto);
  if (data.data?.accessToken) {
    localStorage.setItem('authx_customer_token', data.data.accessToken);
    localStorage.setItem('authx_customer_refresh', data.data.refreshToken);
    localStorage.setItem('authx_customer', JSON.stringify(data.data.customer));
  }
  return data.data;
});

export const fetchWallet = t('loyalty/fetchWallet', async (companyId) => {
  const { data } = await customerApi.get(`Loyalty/wallet?companyId=${companyId}`);
  return data.data;
});

const loadCustomer = () => {
  try {
    return JSON.parse(localStorage.getItem('authx_customer') || 'null');
  } catch {
    return null;
  }
};

const loyaltySlice = createSlice({
  name: 'loyalty',
  initialState: {
    customer: loadCustomer(),
    isAuthenticated: !!getCustomerToken(),
    wallet: null,
    otpSent: false,
    loading: false,
    error: null
  },
  reducers: {
    logoutCustomer: (state) => {
      state.customer = null;
      state.isAuthenticated = false;
      state.wallet = null;
      localStorage.removeItem('authx_customer_token');
      localStorage.removeItem('authx_customer_refresh');
      localStorage.removeItem('authx_customer');
    },
    clearOtpSent: (state) => {
      state.otpSent = false;
    }
  },
  extraReducers: (b) => {
    b.addCase(sendOtp.fulfilled, (s) => {
      s.otpSent = true;
    })
      .addCase(verifyOtp.fulfilled, (s, a) => {
        s.customer = a.payload.customer;
        s.isAuthenticated = true;
        s.otpSent = false;
      })
      .addCase(registerCustomer.fulfilled, (s, a) => {
        s.customer = a.payload.customer;
        s.isAuthenticated = true;
      })
      .addCase(fetchWallet.pending, (s) => {
        s.loading = true;
      })
      .addCase(fetchWallet.fulfilled, (s, a) => {
        s.loading = false;
        s.wallet = a.payload;
      })
      .addCase(fetchWallet.rejected, (s) => {
        s.loading = false;
      });
  }
});

export const { logoutCustomer, clearOtpSent } = loyaltySlice.actions;
export default loyaltySlice.reducer;
